import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api.service';

/**
 * Custom hook for fetching and managing messages with pagination, sorting, and filtering
 */
export const useMessages = ({ search = '', filter = {}, dateRange, messagingServiceSid, broadcastId }) => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [totalRows, setTotalRows] = useState(0);
	const [perPage, setPerPage] = useState(20);
	const [sort, setSort] = useState('createdAt');
	const [sortDirection, setSortDirection] = useState('desc');

	// Build filter object
	const buildFilter = useCallback(() => {
		const finalFilter = { ...filter };

		if (messagingServiceSid) {
			finalFilter.messagingServiceSid = messagingServiceSid;
		}

		if (broadcastId) {
			finalFilter.broadcastId = broadcastId;
		}

		if (dateRange) {
			finalFilter.createdAt = {
				$gte: dateRange.startDate,
				$lt: dateRange.endDate
			};
		}

		return finalFilter;
	}, [filter, messagingServiceSid, broadcastId, dateRange]);

	// Fetch messages
	const fetchMessages = useCallback(async (page = 1) => {
		setLoading(true);
		setError(null);

		try {
			const response = await apiService.messages.getAll({
				page,
				perPage,
				sort,
				sortDirection,
				search,
				filter: buildFilter()
			});

			setData(response.data.data);
			setTotalRows(response.data.total);
		} catch (err) {
			setError(err.message || 'Failed to fetch messages');
			console.error('Error fetching messages:', err);
		} finally {
			setLoading(false);
		}
	}, [perPage, sort, sortDirection, search, buildFilter]);

	// Handle page change
	const handlePageChange = useCallback((page) => {
		fetchMessages(page);
	}, [fetchMessages]);

	// Handle sort change
	const handleSort = useCallback(async (column, newSortDirection) => {
		setSort(column.sortField);
		setSortDirection(newSortDirection);
		setLoading(true);

		try {
			const response = await apiService.messages.getAll({
				page: 1,
				perPage,
				sort: column.sortField,
				sortDirection: newSortDirection,
				search,
				filter: buildFilter()
			});

			setData(response.data.data);
		} catch (err) {
			setError(err.message || 'Failed to sort messages');
			console.error('Error sorting messages:', err);
		} finally {
			setLoading(false);
		}
	}, [perPage, search, buildFilter]);

	// Handle per page change
	const handlePerRowsChange = useCallback(async (newPerPage, page) => {
		setPerPage(newPerPage);
		setLoading(true);

		try {
			const response = await apiService.messages.getAll({
				page,
				perPage: newPerPage,
				sort,
				sortDirection,
				search,
				filter: buildFilter()
			});

			setData(response.data.data);
		} catch (err) {
			setError(err.message || 'Failed to update page size');
			console.error('Error changing page size:', err);
		} finally {
			setLoading(false);
		}
	}, [sort, sortDirection, search, buildFilter]);

	// Fetch on mount and when dependencies change
	useEffect(() => {
		fetchMessages(1);
	}, [search, dateRange, messagingServiceSid, broadcastId]);

	return {
		data,
		loading,
		error,
		totalRows,
		perPage,
		sort,
		sortDirection,
		handlePageChange,
		handleSort,
		handlePerRowsChange,
		refetch: fetchMessages
	};
};
